import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import useAppLinks from '../hooks/useAppLinks';
import { FileText, Shield, HelpCircle, Users, Facebook, Twitter, Instagram, Youtube, Bike, ShoppingBag, BookOpen, Scale, Megaphone, ChevronDown } from 'lucide-react';

const AccordionSection = ({ title, isOpen, onToggle, children }) => (
  <div className="border-b border-zl-brown-500 md:border-0">
    {/* Mobile: clickable header */}
    <button
      type="button"
      onClick={onToggle}
      className="w-full flex items-center justify-between py-3 md:hidden text-left"
    >
      <h3 className="text-sm font-semibold text-zl-yellow">{title}</h3>
      <ChevronDown className={`w-4 h-4 text-zl-brown-200 transition-transform duration-300 ${isOpen ? 'rotate-180' : ''}`} />
    </button>
    {/* Desktop: static header */}
    <h3 className="text-sm font-semibold mb-3 text-zl-yellow hidden md:block">{title}</h3>
    {/* Content: animated on mobile, always visible on desktop */}
    <div
      className={`overflow-hidden transition-all duration-300 ease-in-out md:!max-h-none md:!opacity-100 md:!mb-0 ${
        isOpen ? 'max-h-60 opacity-100 mb-3' : 'max-h-0 opacity-0'
      }`}
    >
      {children}
    </div>
  </div>
);

const Footer = () => {
  const [openSection, setOpenSection] = useState(null);
  const appLinks = useAppLinks();

  const toggle = (key) => setOpenSection(prev => prev === key ? null : key);

  return (
    <footer className="bg-zl-brown text-white" data-testid="footer">
      <div className="max-w-7xl mx-auto px-4 py-8">
        {/* Brand — always visible, prominent on mobile */}
        <div className="mb-6 md:hidden text-center">
          <img src="/logo-zonalibre.png" alt="Zona Libre" className="h-10 w-auto mx-auto mb-2" />
          <p className="text-zl-brown-200 text-xs leading-relaxed">
            Zona Libre es una marca registrada de TrendyShop de Mexico S. de R.L. de C.V.
          </p>
          <div className="flex justify-center space-x-5 mt-3">
            <a href="#" className="text-zl-brown-200 hover:text-zl-yellow transition-colors"><Facebook className="w-5 h-5" /></a>
            <a href="#" className="text-zl-brown-200 hover:text-zl-yellow transition-colors"><Twitter className="w-5 h-5" /></a>
            <a href="#" className="text-zl-brown-200 hover:text-zl-yellow transition-colors"><Instagram className="w-5 h-5" /></a>
            <a href="#" className="text-zl-brown-200 hover:text-zl-yellow transition-colors"><Youtube className="w-5 h-5" /></a>
          </div>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-4 gap-0 md:gap-8">
          {/* Brand — desktop only */}
          <div className="hidden md:block">
            <img src="/logo-zonalibre.png" alt="Zona Libre" className="h-10 w-auto mb-3" />
            <p className="text-zl-brown-200 text-xs leading-relaxed mb-3">
              Zona Libre es una marca registrada de TrendyShop de Mexico S. de R.L. de C.V.
            </p>
            <div className="flex space-x-4">
              <a href="#" className="text-zl-brown-200 hover:text-zl-yellow transition-colors"><Facebook className="w-5 h-5" /></a>
              <a href="#" className="text-zl-brown-200 hover:text-zl-yellow transition-colors"><Twitter className="w-5 h-5" /></a>
              <a href="#" className="text-zl-brown-200 hover:text-zl-yellow transition-colors"><Instagram className="w-5 h-5" /></a>
              <a href="#" className="text-zl-brown-200 hover:text-zl-yellow transition-colors"><Youtube className="w-5 h-5" /></a>
            </div>
          </div>

          {/* Legal */}
          <AccordionSection title="Legal" isOpen={openSection === 'legal'} onToggle={() => toggle('legal')}>
            <div className="flex flex-col space-y-2">
              <Link to="/terminos" className="flex items-center text-zl-brown-200 hover:text-zl-yellow transition-colors text-xs"><FileText className="w-3.5 h-3.5 mr-2 shrink-0" />Terminos y condiciones</Link>
              <Link to="/privacidad" className="flex items-center text-zl-brown-200 hover:text-zl-yellow transition-colors text-xs"><Shield className="w-3.5 h-3.5 mr-2 shrink-0" />Aviso de privacidad</Link>
              <Link to="/acerca" className="flex items-center text-zl-brown-200 hover:text-zl-yellow transition-colors text-xs"><BookOpen className="w-3.5 h-3.5 mr-2 shrink-0" />Acerca de nosotros</Link>
              <Link to="/deslinde" className="flex items-center text-zl-brown-200 hover:text-zl-yellow transition-colors text-xs"><Scale className="w-3.5 h-3.5 mr-2 shrink-0" />Deslinde legal</Link>
            </div>
          </AccordionSection>

          {/* Oportunidades */}
          <AccordionSection title="Oportunidades" isOpen={openSection === 'oportunidades'} onToggle={() => toggle('oportunidades')}>
            <div className="flex flex-col space-y-2">
              <Link to="/vender" className="flex items-center text-zl-brown-200 hover:text-zl-yellow transition-colors text-xs" data-testid="footer-sell-link"><ShoppingBag className="w-3.5 h-3.5 mr-2 shrink-0" />Abre tu tienda</Link>
              <Link to="/delivery/register" className="flex items-center text-zl-brown-200 hover:text-zl-yellow transition-colors text-xs"><Bike className="w-3.5 h-3.5 mr-2 shrink-0" />Se moto repartidor</Link>
              <Link to="/ads" className="flex items-center text-zl-brown-200 hover:text-zl-yellow transition-colors text-xs"><Megaphone className="w-3.5 h-3.5 mr-2 shrink-0" />Anunciate en Zona Libre Ads</Link>
              <Link to="/afiliados" className="flex items-center text-zl-brown-200 hover:text-zl-yellow transition-colors text-xs"><Users className="w-3.5 h-3.5 mr-2 shrink-0" />Programa de afiliados</Link>
              <Link to="/ayuda" className="flex items-center text-zl-brown-200 hover:text-zl-yellow transition-colors text-xs"><HelpCircle className="w-3.5 h-3.5 mr-2 shrink-0" />Centro de ayuda</Link>
            </div>
          </AccordionSection>

          {/* Contacto */}
          <AccordionSection title="Contacto" isOpen={openSection === 'contacto'} onToggle={() => toggle('contacto')}>
            <div className="space-y-2 text-xs text-zl-brown-200">
              <p>Atencion al cliente 24/7</p>
              <p className="text-zl-yellow font-semibold">+52 800-ZONA-LIBRE</p>
              <p>soporte@zonalibre.mx</p>
            </div>
            <div className="mt-4">
              <p className="text-xs text-zl-brown-200 mb-2">Descarga la app</p>
              <div className="flex space-x-2">
                {appLinks?.ios_url && (
                  <a href={appLinks.ios_url} target="_blank" rel="noopener noreferrer" data-testid="footer-appstore-link" className="bg-zl-brown-700 hover:bg-zl-brown-800 text-white text-[10px] px-3 py-1.5 rounded transition-colors">App Store</a>
                )}
                {appLinks?.android_url && (
                  <a href={appLinks.android_url} target="_blank" rel="noopener noreferrer" data-testid="footer-googleplay-link" className="bg-zl-brown-700 hover:bg-zl-brown-800 text-white text-[10px] px-3 py-1.5 rounded transition-colors">Google Play</a>
                )}
              </div>
            </div>
          </AccordionSection>
        </div>
      </div>

      <div className="border-t border-zl-brown-500">
        <div className="max-w-7xl mx-auto px-4 py-4">
          <div className="flex flex-col sm:flex-row items-center justify-between text-[11px] text-zl-brown-300 gap-2">
            <p>&copy; {new Date().getFullYear()} Zona Libre. Todos los derechos reservados. TrendyShop de Mexico S. de R.L. de C.V.</p>
            <div className="flex items-center space-x-3">
              <span className="flex items-center"><Shield className="w-3.5 h-3.5 mr-1 text-green-400" />Compra Protegida</span>
              <span>|</span>
              <span>Envios a todo Mexico</span>
            </div>
          </div>
        </div>
      </div>
    </footer>
  );
};

export default Footer;
